import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter, HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild
} from '@angular/core';
import {ActiveDescendantKeyManager, ListKeyManager} from '@angular/cdk/a11y';
import {ListGroupItemComponent} from './list-group-item/list-group-item.component';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'ds-list-group',
  templateUrl: './list-group.component.html',
  styleUrls: ['./list-group.component.scss'],
})
export class ListGroupComponent implements AfterContentInit, OnDestroy {

  @Input() useFlexSpace = false;
  @Input() maxHeight = 'none';
  @Input() minHeight = '0';
  @Input() allowFocusOutside = false;
  @Input() listTabIndex = '0';
  @Input() listClassName = 'list-group';
  @Input() persistHighlighting = true;
  @Output() elementClick: EventEmitter<string> = new EventEmitter<string>();
  @Output() reachedBoundaryEnd: EventEmitter<void> = new EventEmitter<void>();
  @Output() reachedBoundaryStart: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('listGroupRoot', {static: true}) listGroupRoot: ElementRef;
  @ContentChildren(ListGroupItemComponent) listItems: QueryList<ListGroupItemComponent>;
  public keyboardManager: ListKeyManager<ListGroupItemComponent>;
  refresh$: Subject<boolean> = new Subject();
  @HostBinding('class') class = 'scrollable';

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    this.handleKeyInput(event);
  }

  public focus(): void {
    if (this.listGroupRoot) {
      this.listGroupRoot.nativeElement.focus();
    }
  }

  handleFocus(event: KeyboardEvent): void {
    if (this.allowFocusOutside) {
      this.setListItemsToInactive();
      this.keyboardManager.setActiveItem(-1);
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      if (this.keyboardManager.activeItemIndex < 0) {
        this.keyboardManager.setActiveItem(0);
      }
    }
  }

  handleKeyInput(event: KeyboardEvent): void { // NOSONAR
    if (!this.allowFocusOutside) {
      event.preventDefault();
    }
    if (this.keyboardManager) {
      if (event.code === 'ArrowDown' || event.code === 'ArrowUp') {
        this.keyboardManager.onKeydown(event);
      } else if (event.code === 'Tab') {
        if (event.shiftKey) {
          if (this.keyboardManager.activeItemIndex === 0) {
            this.reachedBoundaryStart.emit();
            if (this.allowFocusOutside) {
              this.keyboardManager.setActiveItem(-1);
              this.setListItemsToInactive();
            }
          }
          this.keyboardManager.setPreviousItemActive();
        } else {
          const previousActiveIndex: number = this.keyboardManager.activeItemIndex;
          this.keyboardManager.setNextItemActive();
          if (this.keyboardManager.activeItemIndex === previousActiveIndex) {
            this.reachedBoundaryEnd.emit();
            if (this.allowFocusOutside) {
              this.keyboardManager.setActiveItem(this.listItems.length);
              this.setListItemsToInactive();
            }
          }
        }
      } else if (event.code === 'Enter') {
        if (this.keyboardManager.activeItem && !this.keyboardManager.activeItem.disabled) {
          this.keyboardManager.activeItem.selectElement();
        }
      }
    }
  }

  resetKeyboardManager(): void {
    this.refresh$.next(true);
    if (this.listItems) {
      this.keyboardManager = new ActiveDescendantKeyManager(this.listItems);
      if (this.persistHighlighting) {
        this.listItems.forEach((listItem) => {
          listItem.elementClick.pipe(
            takeUntil(this.refresh$),
            untilDestroyed(this)
          ).subscribe((value) => {
            const index = this.listItems.find((searchItem) => searchItem.value === value);
            this.keyboardManager.setActiveItem(index);
          });
        });
      }
    }
  }

  ngAfterContentInit(): void {
    if (this.listItems) {
      this.listItems.changes.pipe(untilDestroyed(this)).subscribe(() => {
        this.resetKeyboardManager();
      });
      this.resetKeyboardManager();
    }
  }

  ngOnDestroy(): void {
    this.refresh$.next(true);
    this.refresh$.complete();
  }

  /**
   * Remove the selection of all ListGroupItemComponents
   */
  setListItemsToInactive(): void {
    this.listItems.forEach((listItem) => {
      listItem.setInactiveStyles();
    });
  }
}
